import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button, Modal, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { isMobile } from 'react-device-detect';
import shutterIcon from '../../assets/icons/camera-shutter.webp';
import { collapseModalAtPoint } from '../Helper/PopupCollapse';
import { getFeatureFlag } from '../Service/FeatureFlags';
import { GEOLOCATION_TITLE_FONT_SIZE, GEOLOCATION_ACCURACY_VALUE_FONT_SIZE } from '../Pages/FormPage/GeolocationData';
import { LOADER_TEXT_FONT_SIZE } from './Loader/FullPageLoader';

interface TreeCameraCaptureProps {
  open: boolean;
  // Photo compression started after that the camera popup has completed closing
  onCapture: (encodePhoto: () => Promise<Blob>) => void;
  onCancel: () => void;
  // Called when the live camera cannot be used (no permission, no camera, ...): the caller then
  // offers the file input, which is the only way left to take a photo.
  onUnavailable: () => void;
  // Accuracy of the position, in meters, that the photo will be attached to. It replaces the fixed
  // title so the user sees how good the fix is while shooting, exactly like the "Best Accuracy:" line
  // of the form. `null` is "no fix yet", `undefined` is "the caller has nothing to show".
  accuracy?: number | null;
  // Screen point the modal collapses towards while it closes - the queue item the tree is about to
  // be added to. Without it the modal simply closes into its own centre.
  getCollapsePoint?: () => { x: number; y: number } | null;
  // Called once the closing animation is over. The caller waits for it before compressing the photo
  // and showing its loading indicator, so those only start once the camera is really gone.
  onClosed?: () => void;
}

// The shape the camera is asked for, and the shape of the popup's preview: 16:9.
const PREVIEW_ASPECT = 16 / 9;

// The frame the camera is asked for: a standard 1920x1080 at 16:9, deliberately *not* its largest mode.
// The camera magnifies by cropping a frame it has already produced, and the larger that frame is, the more
// the crop has to be scaled back up: this mode is what keeps the zoom sharp. It costs this popup nothing -
// the preview box is about 640 css px wide and the photo kept is 1280px on its long side, both under 1920
// real pixels.
//
// The height is the width's other side at 16:9: asking for one big number on *both* sides is what gets a
// square stream out of the camera, and the popup then shows a square view.
const CAMERA_FRAME = { width: { ideal: 1920 }, height: { ideal: Math.round(1920 / PREVIEW_ASPECT) } };

// What the popup spends besides the preview: the title line, the shutter and the paddings. Taken off the
// window's height when the preview is sized, so that the whole popup fits
const POPUP_UI_HEIGHT_PX = 160;

// The side paddings antd's body spends on the picture in the boxed window, i.e. its own 24px on each side, taken
// off the width that window leaves the picture. The maximized window zeroes them instead (see
// `camera-popup-maximized` / `-fullscreen`), which is width the picture would otherwise never get.
const BOXED_BODY_SIDE_PADDING_PX = 48;

// The title band of the camera window, as `@camera-title-height` sets it in `assets/less/main.less`. The two have
// to be kept in step: the landscape window's width is worked out from the height the picture is left, and this
// band is part of what is taken off it.
const CAMERA_TITLE_HEIGHT_PX = 34;

// The thin white the window keeps around the picture - the body's own padding, which the `camera-popup-*` classes
// set (8px all round in landscape, 8px top and bottom in portrait).
const CAMERA_BODY_PADDING_PX = 8;

// How narrow the landscape window may get: a small phone's screen, which is what its title line and the shutter
// need. A stream whose shape asks for less (a portrait stream on a landscape screen) keeps a little more white
// either side instead.
const LANDSCAPE_MIN_MODAL_WIDTH_PX = 320;

// The shutter that takes the photo: drawn over the stream rather than in a row of its own, so the picture keeps
// that height, and as large as a thumb reaches - the way every camera app has it.
const SHUTTER_SIZE_PX = 64;
// The white disc behind it, wide enough to leave a rim of white around the picture: a dark shutter
// on a dark stream would otherwise be invisible.
const SHUTTER_DISC_SIZE_PX = 76;
// How far the shutter stands above the bottom edge of the picture.
const SHUTTER_BOTTOM_PX = 16;

// How often the camera may be re-configured while a pinch is running. A move event fires several dozen
// times a second and every one of them re-configures the camera; some Android cameras then hunt for focus
// for the whole gesture and stay soft after it, so the zoom follows the fingers at this interval instead.
const ZOOM_INTERVAL_MS = 120;

// The camera's own zoom (the two-finger pinch) is a feature flag: on where it is being tested - test, staging,
// preprod - and off on production for now (see `FeatureFlags` and the `.env` files, where the CI copies the one
// matching the target into `.env`). `getFeatureFlag` also lets a single visit turn it on with a `?flags=` query,
// which is how it can be tried on production without a build.
const CAMERA_ZOOM_ENABLED = getFeatureFlag('cameraZoom') === true;

// The zoom is the one setting of this popup that outlives it: a planter who zoomed in for the tree he was framing
// photographs the next one the same way. It is written when the popup closes and read when the next stream is
// opened, as an absolute value - the range is asked of the camera again on every open, and the remembered value
// Refreshing the page starts again from no zoom
let rememberedZoom: number | null = null;

// `zoom` and `focusMode` are camera extensions that TypeScript's DOM types still leave out.
type CameraCapabilities = MediaTrackCapabilities & { zoom?: { min?: number; max?: number; step?: number }; focusMode?: string[] };
type CameraConstraintSet = MediaTrackConstraintSet & { zoom?: number; focusMode?: string };
// The same, for the basic constraint set - the one that is authoritative rather than merely attempted.
type CameraConstraints = MediaTrackConstraints & { focusMode?: string };
// The still-pipeline API (`ImageCapture`): TypeScript's DOM types do not declare it, and not every
// browser has it (Firefox has neither the constructor nor `takePhoto`).
type StillCaptureConstructor = new (track: MediaStreamTrack) => { takePhoto(): Promise<Blob> };

// The popup title reuses the two fonts of the form's "Best Accuracy" displayed on main TreeForm page
// (types & colors & sizes included). so accuracy value always stays green (best kept position always <= MINIMUM_ACCURACY
const ACCURACY_LABEL_STYLE: React.CSSProperties = { color: 'rgba(0, 0, 0, 0.45)', fontSize: GEOLOCATION_TITLE_FONT_SIZE, fontWeight: 'normal' };
const ACCURACY_VALUE_STYLE: React.CSSProperties = { color: '#3f8600', fontSize: GEOLOCATION_ACCURACY_VALUE_FONT_SIZE, fontWeight: 'normal' };
// The title line of this popup: the label stays on its left while the value is centred across the
// whole line, so the label is taken out of the flow - otherwise its width would push the value off
// centre. Leaving `top` unset keeps it on the same line as the value, so the two baselines match.
const TITLE_STYLE: React.CSSProperties = { display: 'block', position: 'relative' };
const TITLE_LABEL_STYLE: React.CSSProperties = { ...ACCURACY_LABEL_STYLE, position: 'absolute', left: 0 };
const TITLE_VALUE_STYLE: React.CSSProperties = { ...ACCURACY_VALUE_STYLE, display: 'block', textAlign: 'center' };

/**
 * Live camera capture, used instead of `<input type="file" capture>` on devices that have a camera.
 *
 * With the file input the browser writes the full size photo in its own storage *before* the page
 * gets a `File`, and nothing in the page can delete that copy: it is released whenever the browser
 * decides to, so every shot costs its raw size (1-2 MB) until then. Here a frame copied out of the
 * camera stream comes back as a blob instead, so the device only ever holds the compressed photo the
 * app keeps - and that frame is the very one the preview was showing when the shutter was pressed.
 */
export default function TreeCameraCapture(props: TreeCameraCaptureProps) {
  const { open, onCapture, onCancel, onUnavailable, accuracy, getCollapsePoint, onClosed } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  // Any node inside the modal is enough for the collapse: the panel that carries the closing
  // animation is looked up from it (see `collapseModalAtPoint`).
  const collapseAnchorRef = useRef<HTMLSpanElement>(null);
  // Set by the press that takes the frame, so a second tap on "Take photo" can't happen
  const shotRef = useRef(false);
  const [starting, setStarting] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  // Set once the stream's first frames are flowing: the grey rectangle shows a spinner until then,
  // because `getUserMedia` resolving is not the video: on a phone the first frame takes a moment.
  const [streaming, setStreaming] = useState(false);
  // Width / height of the frames the camera delivers, read from the stream itself and kept up to date for as
  // long as the popup is open: both the preview and the photo are shaped from it
  const [frameAspect, setFrameAspect] = useState<number | undefined>(undefined);
  // The window's size, followed while the popup is open: both the size of the preview and the width of the
  // popup are derived from it (see `previewMaxHeight` / `modalWidth` below).
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });
  useEffect(() => {
    const updateViewport = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    addEventListener('resize', updateViewport);
    addEventListener('orientationchange', updateViewport);
    return () => {
      removeEventListener('resize', updateViewport);
      removeEventListener('orientationchange', updateViewport);
    };
  }, []);
  // Set while the camera's still is being taken, which takes a few hundred milliseconds: the button draws
  // a spinner inside itself for that wait (its label and colour stay exactly as they are), the close
  // button is taken away, and the popup - with the live preview in it - stays open until the photo is in
  // hand.
  const [capturing, setCapturing] = useState(false);
  // The live video track, kept to ask the camera for its zoom (see `applyZoom`).
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);
  // What this camera's zoom allows, when it has one at all: iOS and the usual desktop webcam report
  // no zoom range. `null` until the stream is started.
  const [zoomLimits, setZoomLimits] = useState<{ min: number; max: number } | null>(null);
  // The zoom the camera is currently set to: the touch listeners are attached once and read the ref,
  // so a pinch always starts from where the previous one left the camera.
  const zoomRef = useRef(1);
  // The finger distance the current pinch started from, and the zoom that was set at that moment.
  const pinchRef = useRef<{ startDistance: number; startZoom: number } | null>(null);
  // When the camera was last re-configured during the current pinch (see `ZOOM_INTERVAL_MS`).
  const lastZoomAtRef = useRef(0);

  // The shape of the frames the stream is delivering right now, read off the video element itself. The
  // preview is drawn in that shape, and the photo is cut to it. It is read again every time the element says
  // the stream changed: keeping the shape in portrait or landscape view
  const syncFrameAspect = () => {
    const video = videoRef.current;
    if (video?.videoWidth && video?.videoHeight) {
      setFrameAspect(video.videoWidth / video.videoHeight);
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    let stream: MediaStream | null = null;

    const start = async () => {
      // A fresh stream is about to be opened, with a new track at its own default zoom: the range and the
      // value of the previous one must not survive into it.
      setUnavailable(false);
      setStreaming(false);
      setStarting(true);
      shotRef.current = false;
      videoTrackRef.current = null;
      setZoomLimits(null);
      zoomRef.current = 1;
      setCapturing(false);

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', ...CAMERA_FRAME },
          audio: false,
        });
        if (cancelled) {
          return;
        }

        const track = stream.getVideoTracks()[0];

        // Zoom is the camera's own when it has one: the frame really is magnified, and so is the photo
        // taken from it, instead of the app cropping a wider one. The whole range stays available: the
        // still that `takePhoto` asks for at the press is what keeps a zoomed *photo* at the camera's own
        // photo resolution.
        const zoomRange = (track?.getCapabilities?.() as CameraCapabilities | undefined)?.zoom;
        const zoomMin = zoomRange?.min;
        const zoomMax = zoomRange?.max;
        if (track && typeof zoomMin === 'number' && typeof zoomMax === 'number' && zoomMax > zoomMin) {
          // Where to start: the level the last popup ended on, clamped to what this camera takes (see
          // `rememberedZoom`) - or the bottom of the range, i.e. no zoom at all, which is also what the very
          // first time and a build with the zoom turned off get.
          const initialZoom = CAMERA_ZOOM_ENABLED && rememberedZoom !== null
            ? Math.min(Math.max(rememberedZoom, zoomMin), zoomMax)
            : zoomMin;

          videoTrackRef.current = track;
          setZoomLimits({ min: zoomMin, max: zoomMax });
          zoomRef.current = initialZoom;

          // The photo taken saves the zoom level, so that next photo starts with same zoom
          // The very first time, the stream has no zoom
          // Where the zoom is turned off, nothing is asked of the camera at all
          if (CAMERA_ZOOM_ENABLED) {
            applyZoom(initialZoom);
          }
        }

        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          // Handle dynamically changing stream shape (portrait or landscape)
          video.addEventListener('resize', syncFrameAspect);
          await video.play().catch(() => undefined);
        }
      } catch (error) {
        console.error('Camera could not be started', { error });
        if (!cancelled) {
          setUnavailable(true);
        }
      } finally {
        if (!cancelled) {
          setStarting(false);
        }
      }
    };

    void start();

    return () => {
      cancelled = true;
      // The zoom of the popup opens with same zoom as last phototaken
      if (CAMERA_ZOOM_ENABLED && videoTrackRef.current) {
        rememberedZoom = zoomRef.current;
      }
      videoTrackRef.current = null;
      // The camera must not stay on once the modal is gone. The frame is copied out of the video at the
      // press, before this runs, so there is nothing left to wait for.
      stream?.getTracks().forEach((track) => track.stop());
      const video = videoRef.current;
      if (video) {
        video.removeEventListener('resize', syncFrameAspect);
        video.srcObject = null;
      }
    };
  }, [open]);

  // The same value, wording and fonts as the "Best Accuracy" displayed on TreeForm page (see GeolocationData), kept on a single line
  // The camera popup saves & displays the best kept position since the user has opened the camera
  const getAccuracyTitle = () => {
    if (accuracy === undefined) {
      return <span style={TITLE_VALUE_STYLE}>Take a photo</span>;
    }

    const label = <span style={TITLE_LABEL_STYLE}>Best Accuracy:{' '}</span>;

    if (accuracy === null) {
      return <span style={TITLE_STYLE}>{label}<span style={TITLE_VALUE_STYLE}>Waiting for GPS</span></span>;
    }

    const meters = Math.floor(accuracy);
    return (
      <span style={TITLE_STYLE}>
        {label}
        <span style={TITLE_VALUE_STYLE}>{`${meters} meter${meters > 1 ? 's' : ''}`}</span>
      </span>
    );
  };

  // Zoom asked from the camera itself, so the photo taken below is magnified too and the camera keeps
  // its own focusing. `focusMode` is repeated in the same call because setting a zoom makes some
  // Android cameras fall back to manual focus; the second set is for the cameras that do not know
  // `focusMode` at all, as constraint sets are tried in order and only a satisfiable one is applied.
  // The calls are not serialised here: the browser queues them per track, so the newest value wins.
  const applyZoom = (value: number) => {
    zoomRef.current = value;
    const track = videoTrackRef.current;
    if (!track) {
      return;
    }

    track.applyConstraints({
      advanced: [
        { zoom: value, focusMode: 'continuous' },
        { zoom: value },
      ] as CameraConstraintSet[],
    }).catch((error) => console.error('Camera zoom could not be applied', { error, value }));
  };

  // Setting a zoom is what makes some cameras drop out of continuous focus, and nothing switches it back
  // on by itself: the whole view, and every photo taken from it afterwards, would stay soft. It is asked
  // for in the *basic* constraints, which are authoritative, and only when the camera says it knows the
  // mode - a constraint it cannot satisfy would simply be refused.
  const restoreFocus = () => {
    const track = videoTrackRef.current;
    const focusModes = (track?.getCapabilities?.() as CameraCapabilities | undefined)?.focusMode;

    if (!track || !focusModes?.includes('continuous')) {
      return;
    }

    track.applyConstraints({ focusMode: 'continuous' } as CameraConstraints)
      .catch((error) => console.error('Continuous focus could not be switched back on after zooming', { error }));
  };

  // Pinching the preview zooms the camera itself (see `applyZoom`), so the photo taken from it is
  // magnified too. The listeners are added natively instead of through React's `onTouch*` props
  // because those are attached passively, and a passive listener is not allowed to stop the page from
  // zooming along with the camera.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !zoomLimits || !CAMERA_ZOOM_ENABLED) {
      return;
    }

    const distanceBetween = (touches: TouchList) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 2) {
        return;
      }
      // Nothing of the previous gesture must slow this one down: its very first move applies.
      lastZoomAtRef.current = 0;
      pinchRef.current = { startDistance: distanceBetween(event.touches), startZoom: zoomRef.current };
    };

    const onTouchMove = (event: TouchEvent) => {
      const pinch = pinchRef.current;
      if (!pinch || event.touches.length !== 2 || !pinch.startDistance) {
        return;
      }
      // The gesture belongs to the camera: without this the page would be zoomed as well.
      event.preventDefault();

      // The camera is only re-configured a few times a second (see `ZOOM_INTERVAL_MS`): doing it on every
      // move is what leaves some cameras hunting for focus for the whole gesture.
      const now = Date.now();
      if (now - lastZoomAtRef.current < ZOOM_INTERVAL_MS) {
        return;
      }
      lastZoomAtRef.current = now;

      const scale = distanceBetween(event.touches) / pinch.startDistance;
      applyZoom(Math.min(Math.max(pinch.startZoom * scale, zoomLimits.min), zoomLimits.max));
    };

    // Fingers are lifted one at a time: the pinch only ends when fewer than two remain down.
    const endPinch = (event: TouchEvent) => {
      if (event.touches.length < 2) {
        pinchRef.current = null;
        restoreFocus();
      }
    };

    video.addEventListener('touchstart', onTouchStart, { passive: true });
    video.addEventListener('touchmove', onTouchMove, { passive: false });
    video.addEventListener('touchend', endPinch);
    video.addEventListener('touchcancel', endPinch);

    return () => {
      video.removeEventListener('touchstart', onTouchStart);
      video.removeEventListener('touchmove', onTouchMove);
      video.removeEventListener('touchend', endPinch);
      video.removeEventListener('touchcancel', endPinch);
      pinchRef.current = null;
    };
  }, [zoomLimits]);

  // The frame the preview is showing at this very instant, copied out of the video once and encoded by
  // the caller: the stream is released as soon as the popup closes, so the copy has to be made now,
  // while encoding it takes a few hundred milliseconds and would fight the closing animation.
  //
  // This is the photo as long as the camera is not zoomed (see `takePhoto`). Zoomed, the frame holds
  // fewer real pixels than the camera's still pipeline can give for the same area, so the still is asked
  // for instead - at the cost of holding a slightly later scene than the one that had just been framed.
  const copyVideoFrame = (): (() => Promise<Blob>) | null => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      return null;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    return () => new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        blob ? resolve(blob) : reject(new Error('Failed to process image'));
      }, 'image/jpeg', 0.9);
    });
  };

  // Trims a still to the shape of the preview, so the photo keeps exactly the view that was framed. A
  // still comes back at the camera's photo format - usually 4:3 while the stream is 16:9 - and the extra
  // part of it was never on screen. The crop is centred, which is how a camera fits one format inside the
  // other.
  const cropStillToPreview = async (photo: Blob): Promise<Blob> => {
    // The shape of the preview, remembered from the stream: reading it off the video element here would be
    // too late, because the crop is applied after the popup has been handed over.
    const previewRatio = frameAspect;

    if (!previewRatio || typeof createImageBitmap !== 'function') {
      return photo;
    }

    try {
      const still = await createImageBitmap(photo);
      const stillRatio = still.width / still.height;

      // Same shape already: there is nothing to trim.
      if (Math.abs(stillRatio - previewRatio) < 0.01) {
        still.close();
        return photo;
      }

      // Whichever of the two shapes is the wider one decides which side is trimmed.
      const cropWidth = stillRatio > previewRatio ? Math.round(still.height * previewRatio) : still.width;
      const cropHeight = stillRatio > previewRatio ? still.height : Math.round(still.width / previewRatio);

      const canvas = document.createElement('canvas');
      canvas.width = cropWidth;
      canvas.height = cropHeight;

      const context = canvas.getContext('2d');
      if (!context) {
        still.close();
        return photo;
      }

      context.drawImage(
        still,
        Math.round((still.width - cropWidth) / 2), Math.round((still.height - cropHeight) / 2),
        cropWidth, cropHeight,
        0, 0, cropWidth, cropHeight
      );
      still.close();

      // Re-encoded at a high quality: the compression the app keeps the photo at comes later, and this
      // step must not be what costs the detail the still was taken for.
      return await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          blob ? resolve(blob) : reject(new Error('Failed to process image'));
        }, 'image/jpeg', 0.95);
      });
    } catch (error) {
      console.error('The photo could not be cropped to the view of the preview, keeping the still as it is', { error });
      return photo;
    }
  };

  // The camera's own still: the most detail the device can give, since it goes through the photo pipeline
  // at the sensor's photo resolution instead of through the video one. It is only exposed once the device
  // has switched to that mode, a few hundred milliseconds after the press.
  const captureStill = (): Promise<Blob> => {
    const track = videoTrackRef.current;
    const StillCapture = (window as unknown as { ImageCapture?: StillCaptureConstructor }).ImageCapture;

    if (!track || !StillCapture) {
      return Promise.reject(new Error('no still pipeline available'));
    }

    return new StillCapture(track).takePhoto().then(cropStillToPreview);
  };

  const takePhoto = async () => {
    const video = videoRef.current;
    if (shotRef.current || !video || !video.videoWidth || !video.videoHeight) {
      return;
    }

    // Disarmed as soon as the shot is asked for: a still takes a few hundred milliseconds and the button
    // is still on screen until then.
    shotRef.current = true;

    // Set just before the modal closes - the caller closes it through `onCapture` - so the leave
    // animation collapses the popup towards the show queue item instead of into its own centre.
    const handOver = (photo: () => Promise<Blob>) => {
      collapseModalAtPoint(collapseAnchorRef.current, getCollapsePoint?.() ?? null);
      onCapture(photo);
    };

    // The frame copied now is what the preview is showing, at the moment of the press: it is the photo as
    // long as no zoom has been applied, and the fallback whenever the still below cannot be taken.
    const frame = copyVideoFrame();
    const zoomedIn = !!zoomLimits && zoomRef.current > zoomLimits.min + 0.01;

    if (frame && !zoomedIn) {
      // The shot is already in hand, so nothing is waited for here:
      // the popup stays on screen for its own closing animation, 
      // while inside the shutter is displayed the loading circle animation
      setCapturing(true);
      handOver(frame);
      return;
    }

    // Zoomed in, whatever the level: the camera's own still is asked for instead - the sensor's photo
    // resolution over the area that was framed - and, the device switching to its photo mode first, it
    // takes a few hundred milliseconds. The button shows that wait, and the popup is handed over, and
    // closes, only once the photo is in hand.
    setCapturing(true);
    try {
      const still = await captureStill();
      handOver(() => Promise.resolve(still));
    } catch (error) {
      console.error('The still photo failed, using the frame of the video instead', { error });
      handOver(frame ?? (() => Promise.reject(new Error('Failed to process image'))));
    } finally {
      setCapturing(false);
    }
  };

  // The preview takes every pixel left once the popup's own parts are paid for, 
  // and the window is then made just wide enough to hold it
  //
  // On a phone or a tablet the window is as large as the screen instead: its full width in portrait, where the
  // height keeps a small margin that also centres the window, and in landscape as tall as the screen with its
  // width cut to the picture (see `landscapeModalWidth` and the `camera-popup-*` classes). There the picture is
  // fitted inside the window's own room by `contain`, so it needs none of the sums below - and can be neither
  // cropped nor cut. A desktop window keeps the boxed popup: at most 640px wide and two thirds of the screen tall.
  const maximized = isMobile;
  const edgeToEdge = maximized && viewport.width > viewport.height;
  const frameRatio = frameAspect ?? PREVIEW_ASPECT;
  // Landscape, the window is as wide as the picture it holds and no wider: the picture is as tall as the room the
  // title band and the thin white margin leave - the shutter is drawn over the stream, so it takes none - and as
  // wide as its own shape makes it there. The screen's width caps the window, for a stream whose shape would ask
  // for more. `contain` still decides the picture, so this estimate being off can only leave more or less white,
  // never crop anything.
  const landscapeRoomHeight = Math.max(160, viewport.height - CAMERA_TITLE_HEIGHT_PX - CAMERA_BODY_PADDING_PX * 2);
  const landscapeModalWidth = Math.min(
    viewport.width,
    Math.max(LANDSCAPE_MIN_MODAL_WIDTH_PX, Math.round(landscapeRoomHeight * frameRatio) + CAMERA_BODY_PADDING_PX * 2)
  );
  // What the maximized window's width is set to: the screen's full width in portrait, the picture's own width in
  // landscape.
  const maximizedWidth = edgeToEdge ? landscapeModalWidth : '100%';
  // The room the boxed window can give the picture: the body's width inside a 640px window, and at most two
  // thirds of the screen's height.
  const boxedRoomWidth = Math.max(160, Math.min(640, viewport.width - 32) - BOXED_BODY_SIDE_PADDING_PX);
  const boxedRoomHeight = Math.max(160, Math.min(viewport.height * 0.6, viewport.height - POPUP_UI_HEIGHT_PX));
  // The largest picture of the stream's own shape that room can hold: the frame is never cropped, so whichever
  // of the two sides runs out first is the one that decides the size.
  const previewMaxWidth = Math.min(boxedRoomWidth, boxedRoomHeight * frameRatio);
  const previewMaxHeight = previewMaxWidth / frameRatio;
  const modalWidth = Math.min(640, viewport.width - 32, previewMaxWidth + 48);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      afterClose={onClosed}
      title={getAccuracyTitle()}
      footer={null}
      maskClosable={false}
      // The window must not be closed out from under a still that is being taken.
      closable={!capturing}
      destroyOnClose={true}
      // As wide as the preview needs, never wider than a fixed 640px window or the screen itself (see `modalWidth`)
      // - and the whole screen instead when the window is maximized, where the width comes from its own class.
      width={maximized ? maximizedWidth : modalWidth}
      // Centred, not left at antd's default 100px offset: a landscape phone cannot spare that much of its
      // height in front of the popup. Full screen, the offset is zeroed by that same class.
      centered={!edgeToEdge}
      wrapClassName={edgeToEdge ? 'camera-popup-fullscreen' : maximized ? 'camera-popup-maximized' : undefined}
      bodyStyle={{ touchAction: 'auto' }}
    >
      {/* Any node inside the window is enough for the collapse, see `collapseModalAtPoint`. */}
      <span ref={collapseAnchorRef} style={{ display: 'none' }} />
      {/* Full page loader & Camera popup loader are always the same */}
      {/* One rectangle: the window carries the grey itself while there is no picture to show (loading,
          or no camera at all), and nothing inside it paints a background of its own - the picture fills
          the window exactly once the frames are flowing, so a stream whose shape is capped leaves
          nothing drawn beside it. */}
      <div style={{
        position: 'relative', borderRadius: 4, overflow: 'hidden', minHeight: 160,
        // Maximized, that room is shared out with the shutter: the picture takes what is left and is centred in
        // it (see the `camera-popup-*` classes).
        ...(maximized ? { flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}),
        backgroundColor: streaming ? undefined : 'rgba(0, 0, 0, 0.7)'
      }}>
        <video
          ref={videoRef}
          autoPlay={true}
          playsInline={true}
          muted={true}
          onPlaying={() => setStreaming(true)}
          // The shape of the frames, as soon as the stream says what it delivers in portrait or landscape view
          onLoadedMetadata={syncFrameAspect}
          style={{
            display: 'block',
            width: '100%',
            margin: '0 auto',
            // Maximized, the element IS the room the window leaves and `contain` fits the whole frame inside it:
            // the picture is then as large as that room allows, centred, and can be neither cropped nor cut -
            // whatever the stream's shape, and whatever the title and the shutter really take. The boxed window
            // instead gives the element the shape of the frames, capped to what it can show, so that the picture
            // fills it exactly.
            height: maximized ? '100%' : undefined,
            aspectRatio: maximized ? undefined : frameRatio,
            maxWidth: maximized ? undefined : previewMaxWidth,
            maxHeight: maximized ? undefined : previewMaxHeight,
            objectFit: 'contain',
          }}
        />
        {!streaming && !unavailable && (
          <div className={'loader'} style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            // Nothing to press in there: the overlay must not eat a tap meant for the buttons below.
            pointerEvents: 'none'
          }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} size="large" />
            <span style={{ color: 'white', fontSize: LOADER_TEXT_FONT_SIZE - 2, marginTop: 20, fontWeight: 'bold' }}>Loading...</span>
          </div>
        )}
        {/* The shutter, over the stream's bottom edge: the picture is what the user is framing, so the one control
            sits on it rather than beside it. Its own picture is all that shows of it - no antd blue, no border, no
            shadow - on a white disc, which is what keeps it visible over a dark stream. */}
        {!unavailable && (
          <Button
            onClick={takePhoto}
            disabled={starting || capturing}
            // The label is an image, so the button needs one of its own.
            aria-label="Take photo"
            style={{
              position: 'absolute',
              left: '50%',
              bottom: SHUTTER_BOTTOM_PX,
              transform: 'translateX(-50%)',
              // antd's blue, border and shadow are all off: only the picture shows. The size is the button's own -
              // the disc's, the picture being centred in it - so the whole shutter is what answers the press, and
              // the press is faded as a whole while the stream is starting.
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: SHUTTER_DISC_SIZE_PX,
              height: SHUTTER_DISC_SIZE_PX,
              borderRadius: '50%',
              padding: 0,
              // Solid white disc around the shutter: what separates a dark shutter from a dark stream.
              background: 'rgb(255, 255, 255)',
              border: 'none',
              boxShadow: 'none',
              opacity: starting ? 0.3 : undefined,
            }}
          >
            {/* A zoomed shot goes through the camera's still, which takes a few hundred milliseconds (see
                `takePhoto`) - and every other shot keeps this spinner up for the popup's own closing animation,
                there being nothing else to show for it. The shutter is only hidden, not removed: the control
                keeps its position and its size while this lasts. The press disables the button - the shot is already
                taken (`shotRef` disarmed it) - so a second tap cannot even reach `takePhoto' */}
            <img
              src={shutterIcon}
              alt=""
              style={{
                display: 'block',
                width: SHUTTER_SIZE_PX,
                height: SHUTTER_SIZE_PX,
                visibility: capturing ? 'hidden' : undefined,
              }}
            />
            {capturing && (
              <span style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* The stream can be anything from a white wall to a dark room: the app's own blue is the one colour
                    that reads on both. */}
                <LoadingOutlined spin style={{ color: '#1890ff' }} />
              </span>
            )}
          </Button>
        )}
      </div>
      {unavailable && (
        <>
          <p style={{ color: '#cf1322', marginTop: 12 }}>
            The camera could not be started. Use the button below to take the photo with the camera app instead.
          </p>
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
            <Button type="primary" onClick={onUnavailable}>Use the camera app</Button>
          </div>
        </>
      )}
    </Modal>
  );
}
